import {
  Required,
  IsString,
  IsNumber,
  IsBoolean,
  DataValidator,
  transformKeys,
  ValidateNested,
} from "@/utils/type-vaildator";
import api from "./api";

const URL = "/video/callback/result";

interface VideoCallbackResultProps {
  taskId: string;
}

interface VideoCallbackResultParams {
  task_id: string;
}

interface VideoCallbackResultResponse {
  actor: {
    actor_model_id: string;
    age_group: string;
    compressed_actor_default_video_url: string;
    full_actor_default_video_url: string;
    gender: string;
    id: string;
    image_url: string;
    is_exclusive_pro: boolean;
    is_favorite: boolean;
    is_free_speech: boolean;
    is_hd: boolean;
    is_my_actor: boolean;
    is_pro: boolean;
    name: string;
    situation: string;
    sort_order: number;
    tags: null;
    use_count: number;
  };
  compressed_video_url: string;
  created_at: string;
  id: string;
  input_text: string;
  process_result: string;
  task_id: string;
  updated_at: string;
  video_desc: string;
  video_name: string;
  video_status: number;
  video_url: string;
  voice: {
    age_group: string;
    audio_url: string;
    duration: number;
    emotion: string;
    gender: string;
    id: string;
    is_selected: boolean;
    is_starred: boolean;
    language: string;
    model_name: string;
    name: string;
    script: string;
    sort_order: number;
    speed: number;
    type: number;
    use_count: number;
    video_id: string;
    voice_tts_id: string;
  };
}

// 嵌套对象类 - Actor
class Actor {
  @Required("Actor model ID cannot be empty")
  @IsString("Actor model ID must be a string")
  actorModelId: string;

  @Required("ID cannot be empty")
  @IsString("ID must be a string")
  id: string;

  @Required("Image URL cannot be empty")
  @IsString("Image URL must be a string")
  imageUrl: string;

  @Required("Name cannot be empty")
  @IsString("Name must be a string")
  name: string;
}

// 嵌套对象类 - Voice
class Voice {
  @Required("Audio URL cannot be empty")
  @IsString("Audio URL must be a string")
  audioUrl: string;

  @Required("ID cannot be empty")
  @IsString("ID must be a string")
  id: string;

  @Required("Is starred cannot be empty")
  @IsBoolean("Is starred must be a boolean")
  isStarred: boolean;

  @Required("Model name cannot be empty")
  @IsString("Model name must be a string")
  modelName: string;

  @Required("Name cannot be empty")
  @IsString("Name must be a string")
  name: string;

  @Required("Speed cannot be empty")
  @IsNumber("Speed must be a number")
  speed: number;

  @Required("Voice TTS ID cannot be empty")
  @IsString("Voice TTS ID must be a string")
  voiceTtsId: string;

  @Required("Age group cannot be empty")
  @IsString("Age group must be a string")
  ageGroup: string;

  @Required("Language cannot be empty")
  @IsString("Language must be a string")
  language: string;

  @Required("Gender cannot be empty")
  @IsString("Gender must be a string")
  gender: string;
}

// 主数据类
export class VideoCallbackResultData {
  @Required("Actor cannot be empty")
  @ValidateNested(Actor)
  actor: Actor;

  @IsString("Compressed video URL must be a string")
  compressedVideoUrl: string;

  @Required("Created at cannot be empty")
  @IsString("Created at must be a string")
  createdAt: string;

  @Required("ID cannot be empty")
  @IsString("ID must be a string")
  id: string;

  @Required("Input text cannot be empty")
  @IsString("Input text must be a string")
  inputText: string;

  @Required("Task ID cannot be empty")
  @IsString("Task ID must be a string")
  taskId: string;

  @Required("Video status cannot be empty")
  @IsNumber("Video status must be a number")
  videoStatus: number;

  @IsString("Video URL must be a string")
  videoUrl: string;

  @Required("Voice cannot be empty")
  @ValidateNested(Voice)
  voice: Voice;
}

const transformAndValidateVideoCallbackResult = (
  response: VideoCallbackResultResponse
): VideoCallbackResultData => {
  const transformedData = transformKeys(response);

  try {
    return DataValidator.validateOrThrow(transformedData, VideoCallbackResultData);
  } catch (error) {
    console.error("Video callback result data validation failed:", error.message);
    throw error;
  }
};

export const videoCallbackResult = async (
  props: VideoCallbackResultProps
): Promise<VideoCallbackResultData> => {
  const params: VideoCallbackResultParams = {
    task_id: props.taskId,
  };
  const response = await api.post<VideoCallbackResultResponse>(URL, params);
  const validatedData = transformAndValidateVideoCallbackResult(response.data);
  return validatedData;
};
